import {
  getCustomerMarketDataSnapshotRequestKey,
  type CustomerMarketDataRequestParamsContext,
  type CustomerSecurityMarketDataColumnParams,
  type UseMultiCustomerMarketDataSnapshotRequest,
} from '@talos/kyoko';
import type { Column } from 'ag-grid-community';
import type { PriceGridEntity } from '../PriceGridEntity';
import { PRICE_GRID_CUSTOMER_MARKET_DATA_COLUMN_TYPE } from './constants';
import type { PriceGridMarketDataRequestsParams } from './types';
import { getPriceGridCellSymbol, getPriceGridColumnParams, getPriceGridMarketDataRequestParams } from './utils';

function getCustomerSecurityMarketDataColumnParams(column: Column): CustomerSecurityMarketDataColumnParams | undefined {
  return getPriceGridColumnParams<CustomerSecurityMarketDataColumnParams>(
    column,
    PRICE_GRID_CUSTOMER_MARKET_DATA_COLUMN_TYPE
  );
}

export function getPriceGridCustomerMarketDataRequestParams({ data, symbol }: CustomerMarketDataRequestParamsContext) {
  return getPriceGridMarketDataRequestParams({ data, symbol });
}

export function getPriceGridCustomerMarketDataRequest(
  column: Column,
  item: PriceGridEntity
): UseMultiCustomerMarketDataSnapshotRequest | undefined {
  const symbol = getPriceGridCellSymbol(column, item);
  if (!symbol) {
    return undefined;
  }

  const columnParams = getCustomerSecurityMarketDataColumnParams(column);
  const requestParamsWithCounterparty = columnParams?.getRequestParams?.({
    data: item,
    symbol,
  });
  if (!requestParamsWithCounterparty?.Counterparty) {
    return undefined;
  }

  const { Counterparty: counterparty, ...requestParams } = requestParamsWithCounterparty;
  const key = getCustomerMarketDataSnapshotRequestKey({
    Symbol: symbol,
    Counterparty: counterparty,
    ...requestParams,
  });
  return { key, Symbol: symbol, Counterparty: counterparty, ...requestParams };
}

export function buildPriceGridCustomerMarketDataRequests({
  visibleColumns,
  renderedItems,
}: PriceGridMarketDataRequestsParams): UseMultiCustomerMarketDataSnapshotRequest[] {
  const requestsByKey = new Map<string, UseMultiCustomerMarketDataSnapshotRequest>();

  for (const column of visibleColumns) {
    for (const item of renderedItems) {
      const requestDetails = getPriceGridCustomerMarketDataRequest(column, item);
      if (requestDetails) {
        requestsByKey.set(requestDetails.key, requestDetails);
      }
    }
  }

  return [...requestsByKey.values()];
}
